import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  FormControl,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  Tooltip,
  Divider,
  Grid
} from '@mui/material';
import {
  Twitter as TwitterIcon,
  AddPhotoAlternate as UploadIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  ColorLens as BackgroundIcon,
  Person as PersonIcon,
  Image as ImageIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as ListBulletedIcon,
  FormatListNumbered as ListNumberedIcon,
  FormatColorText as ColorTextIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toPng } from 'html-to-image';
import axios from 'axios';

// --- Add Font Whitelist --- 
const Font = Quill.import('formats/font');
// Use CSS-safe values (lowercase, hyphenated) for the font list
const fontValues = ['arial', 'comic-sans-ms', 'courier-new', 'georgia', 'helvetica', 'lucida-sans', 'tahoma', 'times-new-roman', 'trebuchet-ms', 'verdana']; 
Font.whitelist = fontValues; // Register the CSS-safe values
Quill.register(Font, true);
// --- End Font Whitelist ---

const backgroundStyles = [
  { 
    value: 'custom-image', 
    label: 'Custom Image',
    preview: (color, secondary, customBgImage) => ({
      backgroundColor: color,
      backgroundImage: customBgImage ? `url(${customBgImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    })
  },
  { 
    value: 'solid', 
    label: 'Solid Color',
    preview: (color) => ({
      backgroundColor: color
    })
  },
  { 
    value: 'gradient-linear', 
    label: 'Linear Gradient',
    preview: (color) => ({
      background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
    })
  },
  {
    value: 'gradient-radial', 
    label: 'Radial Gradient',
    preview: (color) => ({
      background: `radial-gradient(circle, ${color} 0%, ${color}dd 100%)`
    })
  },
  { 
    value: 'grid', 
    label: 'Grid Pattern',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `linear-gradient(${secondary}22 1px, transparent 1px), linear-gradient(90deg, ${secondary}22 1px, transparent 1px)`,
      backgroundSize: '10px 10px'
    })
  },
  { 
    value: 'dots', 
    label: 'Dots Pattern',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `radial-gradient(${secondary}33 1px, transparent 1px)`,
      backgroundSize: '8px 8px'
    })
  },
  {
    value: 'diagonal-stripes',
    label: 'Diagonal Stripes',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `repeating-linear-gradient(45deg, ${secondary}11 0px, ${secondary}11 2px, transparent 2px, transparent 10px)`
    })
  },
  {
    value: 'waves',
    label: 'Waves',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `radial-gradient(circle at 100% 150%, ${color} 24%, ${secondary}22 25%, ${secondary}22 28%, ${color} 29%, ${color} 36%, ${secondary}22 36%, ${secondary}22 40%, transparent 40%, transparent),
        radial-gradient(circle at 0 150%, ${color} 24%, ${secondary}22 25%, ${secondary}22 28%, ${color} 29%, ${color} 36%, ${secondary}22 36%, ${secondary}22 40%, transparent 40%, transparent)`,
      backgroundSize: '20px 40px',
      backgroundPosition: '0 0, 20px 0'
    })
  },
  {
    value: 'hexagons',
    label: 'Hexagons',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `linear-gradient(30deg, ${secondary}11 12%, transparent 12.5%, transparent 87%, ${secondary}11 87.5%, ${secondary}11),
        linear-gradient(150deg, ${secondary}11 12%, transparent 12.5%, transparent 87%, ${secondary}11 87.5%, ${secondary}11),
        linear-gradient(30deg, ${secondary}11 12%, transparent 12.5%, transparent 87%, ${secondary}11 87.5%, ${secondary}11),
        linear-gradient(150deg, ${secondary}11 12%, transparent 12.5%, transparent 87%, ${secondary}11 87.5%, ${secondary}11),
        linear-gradient(60deg, ${secondary}22 25%, transparent 25.5%, transparent 75%, ${secondary}22 75%, ${secondary}22),
        linear-gradient(60deg, ${secondary}22 25%, transparent 25.5%, transparent 75%, ${secondary}22 75%, ${secondary}22)`,
      backgroundSize: '20px 35px',
      backgroundPosition: '0 0, 0 0, 10px 18px, 10px 18px, 0 0, 10px 18px'
    })
  },
  {
    value: 'confetti',
    label: 'Confetti',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `
        radial-gradient(${secondary}22 3px, transparent 3px),
        radial-gradient(${secondary}22 3px, transparent 3px),
        radial-gradient(${secondary}33 3px, transparent 3px),
        radial-gradient(${secondary}33 3px, transparent 3px)`,
      backgroundSize: '30px 30px',
      backgroundPosition: '0 0, 15px 15px, 7px 2px, 22px 17px'
    })
  },
  {
    value: 'circuit',
    label: 'Circuit',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `
        linear-gradient(${secondary}22 1px, transparent 1px),
        linear-gradient(90deg, ${secondary}22 1px, transparent 1px),
        linear-gradient(${secondary}11 0.5px, transparent 0.5px),
        linear-gradient(90deg, ${secondary}11 0.5px, ${color} 0.5px)`,
      backgroundSize: '20px 20px, 20px 20px, 5px 5px, 5px 5px'
    })
  },
  {
    value: 'bubbles',
    label: 'Bubbles',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `
        radial-gradient(circle at 25px 25px, ${secondary}22 2%, transparent 2%),
        radial-gradient(circle at 75px 75px, ${secondary}22 2%, transparent 2%)`,
      backgroundSize: '100px 100px'
    })
  },
  {
    value: 'zigzag',
    label: 'Zigzag',
    preview: (color, secondary) => ({
      backgroundColor: color,
      backgroundImage: `
        linear-gradient(135deg, ${secondary}22 25%, transparent 25%) -10px 0,
        linear-gradient(225deg, ${secondary}22 25%, transparent 25%) -10px 0,
        linear-gradient(315deg, ${secondary}22 25%, transparent 25%),
        linear-gradient(45deg, ${secondary}22 25%, transparent 25%)`,
      backgroundSize: '20px 20px'
    })
  }
];

// Nested TweetPreview component
const TweetPreview = ({ 
  tweetText, 
  tweetName, 
  tweetProfileImagePreview, 
  isVerified, 
  tweetTimestamp,
  tweetSize,
  bgColor,
  patternColor,
  textColor,
  selectedStyle,
  customBgImage,
  previewRef,
  sizes,
  backgroundStyles
}) => {
  const formatTweetText = (text) => {
    if (!text) return 'Your tweet will appear here...';
    return text;
  };

  const style = backgroundStyles.find(s => s.value === selectedStyle);
  if (!style) {
    console.error('Style not found:', selectedStyle);
  }

  const currentStyle = style || backgroundStyles[0];

  const currentSize = sizes.find(s => s.value === tweetSize) || sizes[0];
  
  // Get the background styles based on the current style
  const getBackgroundStyles = () => {
    if (selectedStyle === 'custom-image' && customBgImage) {
      console.log('Applying custom background image:', customBgImage);
      return {
        backgroundImage: `url("${customBgImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: bgColor
      };
    }
    const styles = currentStyle.preview(bgColor, patternColor);
    console.log('Applied background styles:', styles);
    return styles;
  };
  
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
      }}
    >
      <Box
        ref={previewRef}
        className="tweet-preview-container"
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '550px',
          aspectRatio: `${currentSize.aspect}`,
          borderRadius: '8px',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(selectedStyle === 'custom-image' && customBgImage ? {
            backgroundImage: `url("${customBgImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: bgColor
          } : getBackgroundStyles())
        }}
      >
        <Box
          className="tweet-content"
          sx={{ 
            width: '80%',
            backgroundColor: '#ffffff',
            border: '1px solid #eff3f4',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Avatar 
              src={tweetProfileImagePreview} 
              sx={{ 
                width: 48, 
                height: 48,
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              {!tweetProfileImagePreview && (tweetName?.[0]?.toUpperCase() || 'U')}
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {tweetName && (
                  <Typography 
                    variant="subtitle2" 
                    component="span"
                    sx={{ 
                      fontWeight: 700,
                      fontSize: '15px',
                      color: '#0f1419'
                    }}
                  >
                    {tweetName}
                  </Typography>
                )}
                
                {isVerified && (
                  <Box 
                    component="img"
                    src="/images/twitter.png"
                    alt="Verified"
                    sx={{ 
                      ml: 0.5,
                      width: 17,
                      height: 17,
                      display: 'inline-flex',
                      objectFit: 'contain'
                    }}
                  />
                )}
                
                {tweetTimestamp && (
                    <Typography 
                      variant="body2" 
                      component="span"
                      sx={{ 
                        ml: 0.5,
                        color: '#536471',
                        fontSize: '14px'
                      }}
                    >
                      {tweetTimestamp}
                    </Typography>
                  )}
              </Box>
              
              <Box 
                sx={{ 
                  fontSize: '15px',
                  color: '#0f1419',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                  '& p': { margin: 0, padding: 0 }, 
                  '& ol, & ul': { paddingLeft: '1.5em' }, 
                  '& a': { color: '#1d9bf0', textDecoration: 'none' }, 
                  '& strong, & b': { fontWeight: 'bold' },
                  '& em, & i': { fontStyle: 'italic' },
                  '& .ql-size-small': { fontSize: '0.75em' },
                  '& .ql-size-large': { fontSize: '1.5em' },
                  '& .ql-size-huge': { fontSize: '2.5em' },
                  '& .ql-font-arial': { fontFamily: 'Arial, sans-serif' },
                  '& .ql-font-comic-sans-ms': { fontFamily: '\'Comic Sans MS\', cursive, sans-serif' },
                  '& .ql-font-courier-new': { fontFamily: '\'Courier New\', Courier, monospace' },
                  '& .ql-font-georgia': { fontFamily: 'Georgia, serif' },
                  '& .ql-font-helvetica': { fontFamily: 'Helvetica, sans-serif' },
                  '& .ql-font-lucida-sans': { fontFamily: '\'Lucida Sans Unicode\', \'Lucida Grande\', sans-serif' },
                  '& .ql-font-tahoma': { fontFamily: 'Tahoma, sans-serif' },
                  '& .ql-font-times-new-roman': { fontFamily: '\'Times New Roman\', Times, serif' },
                  '& .ql-font-trebuchet-ms': { fontFamily: '\'Trebuchet MS\', sans-serif' },
                  '& .ql-font-verdana': { fontFamily: 'Verdana, sans-serif' }
                }}
                dangerouslySetInnerHTML={{ __html: formatTweetText(tweetText) }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const TweetGenerator = forwardRef(({
  onSaveToPost, 
  onStateChange, 
  showPreview = false, 
  previewOnly = false,
  hideEditor = false,
  onTextChange,
  tweetText: propTweetText = '<p>Your thoughts shape your world. Share them one post at a time.</p><p><br></p><p>— Instation</p><p><br></p><p>#TweetStyle #BuildInPublic #DailyThoughts</p>', 
  tweetName: propTweetName = 'Instation',
  tweetProfileImagePreview: propTweetProfileImagePreview = '/images/instation-logo.png',
  isVerified: propIsVerified = true,
  tweetTimestamp: propTweetTimestamp = '5m ago',
  tweetSize: propTweetSize = 'square',
  bgColor: propBgColor,
  patternColor: propPatternColor,
  textColor: propTextColor,
  selectedStyle: propSelectedStyle = 'gradient-linear',
  customBgImage: propCustomBgImage = null,
  sizes,
  backgroundStyles
}, ref) => {
  const [tweetText, setTweetText] = useState(propTweetText);
  const [tweetName, setTweetName] = useState(propTweetName);
  const [tweetProfileImage, setTweetProfileImage] = useState(null);
  const [tweetProfileImagePreview, setTweetProfileImagePreview] = useState(propTweetProfileImagePreview);
  const [isVerified, setIsVerified] = useState(propIsVerified);
  const [tweetTimestamp, setTweetTimestamp] = useState(propTweetTimestamp);
  const [forceRender, setForceRender] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [linkedinProfileData, setLinkedinProfileData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  const tweetRef = useRef(null);
  const containerRef = useRef(null);
  const internalPreviewRef = useRef(null);
  const activePreviewRef = internalPreviewRef;

  // Update useEffect to include state changes notification
  useEffect(() => {
    if (onStateChange) {
      const currentState = {
        tweetText,
        tweetName,
        tweetProfileImagePreview,
        isVerified,
        tweetTimestamp,
        tweetSize: propTweetSize,
        bgColor: propBgColor,
        patternColor: propPatternColor,
        textColor: propTextColor,
        selectedStyle: propSelectedStyle,
        customBgImage: propCustomBgImage
      };
      console.log('Notifying parent of state update:', currentState);
      onStateChange(currentState);
    }
  }, [
    tweetText,
    tweetName,
    tweetProfileImagePreview,
    isVerified,
    tweetTimestamp,
    propTweetSize,
    propBgColor,
    propPatternColor,
    propTextColor,
    propSelectedStyle,
    propCustomBgImage,
    onStateChange
  ]);

  // Add useEffect to fetch user profile and LinkedIn data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const userData = response.data.user;
        setUserProfile(userData);

        if (userData.linkedinProfile) {
          // Fetch LinkedIn profile data
          const linkedinResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/linkedin/profile-data`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          setLinkedinProfileData(linkedinResponse.data);
          // Update display name and profile image from LinkedIn data
          setTweetName(linkedinResponse.data.name || userData.name);
          setTweetProfileImagePreview(linkedinResponse.data.profile_picture || userData.image);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();
  }, []);

  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setTweetProfileImage(file);
    
    const reader = new FileReader();
    reader.onload = () => {
      setTweetProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Expose handleDownload and handleSaveToPost via the forwarded ref
  useImperativeHandle(ref, () => ({
    handleDownload,
    handleSaveToPost
  }));

  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      // Try multiple ways to find the preview container
      let previewContainer = null;
      
      // First try using querySelector
      previewContainer = document.querySelector('.tweet-preview-container');
      
      // If not found, try using activePreviewRef
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
        console.log("Using ref instead of class selector for download");
      }
      
      if (!previewContainer) {
        console.error("Tweet preview container not found by any method");
        throw new Error('Tweet preview container not found');
      }
      
      console.log("Found preview container:", previewContainer);
      
      // Generate the image using html-to-image
      // Let the library determine the dimensions from the element's actual appearance
      const dataUrl = await toPng(previewContainer, {
        quality: 1.0,
        pixelRatio: 3, // Higher resolution
        cacheBust: true,
        skipFonts: false
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `tweet-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      
    } catch (error) {
      console.error('Error generating tweet image:', error);
      alert('There was an error generating the tweet image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleSaveToPost = async () => {
    try {
      setSaving(true);
      
      // Try multiple ways to find the preview container
      let previewContainer = null;
      
      // First try using querySelector
      previewContainer = document.querySelector('.tweet-preview-container');
      
      // If not found, try using activePreviewRef
      if (!previewContainer && activePreviewRef && activePreviewRef.current) {
        previewContainer = activePreviewRef.current;
        console.log("Using ref instead of class selector for save to post");
      }
      
      if (!previewContainer) {
        console.error("Tweet preview container not found by any method for save to post");
        throw new Error('Tweet preview container not found');
      }
      
      console.log("Found preview container for save:", previewContainer);
      
      // Generate the image using html-to-image
      // Let the library determine the dimensions from the element's actual appearance
      const dataUrl = await toPng(previewContainer, {
        quality: 1.0,
        pixelRatio: 3, // Higher resolution
        cacheBust: true,
        skipFonts: false
      });
      
      // Convert data URL to blob
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      
      // Pass blob to parent component
      if (onSaveToPost) {
        onSaveToPost(blob);
      }
      
    } catch (error) {
      console.error('Error preparing tweet image:', error);
      alert('There was an error preparing the tweet image. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Render preview with current state
  const renderPreview = () => {
    return (
      <TweetPreview
        key={`preview-${propBgColor}-${propPatternColor}-${propSelectedStyle}-${forceRender}`}
        tweetText={previewOnly ? propTweetText : tweetText}
        tweetName={previewOnly ? propTweetName : tweetName}
        tweetProfileImagePreview={previewOnly ? propTweetProfileImagePreview : tweetProfileImagePreview}
        isVerified={previewOnly ? propIsVerified : isVerified}
        tweetTimestamp={previewOnly ? propTweetTimestamp : tweetTimestamp}
        tweetSize={propTweetSize}
        bgColor={propBgColor}
        patternColor={propPatternColor}
        textColor={propTextColor}
        selectedStyle={propSelectedStyle}
        customBgImage={propCustomBgImage}
        previewRef={activePreviewRef}
        sizes={sizes}
        backgroundStyles={backgroundStyles}
      />
    );
  };

  // If previewOnly is true, only render the TweetPreview component
  if (previewOnly) {
    const currentSize = sizes.find(s => s.value === propTweetSize) || sizes[0];
    return (
      <Box ref={containerRef} sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0 }}>
        <Box sx={{
            width: '100%',
            maxWidth: '550px',
            height: 'auto',
            position: 'relative',
            '&::before': {
              content: '""',
              display: 'block',
             paddingTop: `${(currentSize.height / currentSize.width) * 100}%`,
          }
        }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {renderPreview()}
          </Box>
        </Box>
      </Box>
    );
  }

  // Define Quill modules and formats 
  const quillModules = {
    toolbar: [
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'font': fontValues }], 
      ['bold', 'italic', 'underline', 'strike'], 
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{ 'color': [] }, { 'background': [] }], 
      ['link'], 
      ['clean'] 
    ],
  };

  const quillFormats = [
    'size', 'font',
    'bold', 'italic', 'underline', 'strike', 
    'list', 'bullet',
    'color', 'background',
    'link'
  ];

  return (
    <Box ref={containerRef} sx={{ display: 'flex', flexDirection: { xs: 'column', md: showPreview ? 'column' : 'row' }, gap: 2, width: '100%', height: '100%' }}>
      {/* Control Panel */}
      <Box sx={{
        flex: showPreview ? 1 : '1',
        display: 'flex',
        flexDirection: 'column',
        px: 1.5, pb: 1.5,
        borderRight: showPreview ? 'none' : '1px solid rgba(0,0,0,0.08)'
      }}>
        {/* Content area - no scrolling needed */}
        <Box
          sx={{
            flex: 1,
            pr: 0.5,
          }}
        >
          {/* Tweet Content */}
          {!hideEditor && (
            <>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  mb: 1.5, 
                  fontWeight: 600, 
                  color: '#4A5568',
                  fontSize: '0.95rem' 
                }}
              >
                Tweet Content
              </Typography>
              
              {/* Replace contentEditable Box with ReactQuill */}
              <Box sx={{ 
                mb: 3, 
                // Add specific styles for Quill font classes in the editor content
                '& .ql-editor .ql-font-arial': { fontFamily: 'Arial, sans-serif' },
                '& .ql-editor .ql-font-comic-sans-ms': { fontFamily: '\'Comic Sans MS\', cursive, sans-serif' },
                '& .ql-editor .ql-font-courier-new': { fontFamily: '\'Courier New\', Courier, monospace' },
                '& .ql-editor .ql-font-georgia': { fontFamily: 'Georgia, serif' },
                '& .ql-editor .ql-font-helvetica': { fontFamily: 'Helvetica, sans-serif' },
                '& .ql-editor .ql-font-lucida-sans': { fontFamily: '\'Lucida Sans Unicode\', \'Lucida Grande\', sans-serif' }, 
                '& .ql-editor .ql-font-tahoma': { fontFamily: 'Tahoma, sans-serif' },
                '& .ql-editor .ql-font-times-new-roman': { fontFamily: '\'Times New Roman\', Times, serif' },
                '& .ql-editor .ql-font-trebuchet-ms': { fontFamily: '\'Trebuchet MS\', sans-serif' },
                '& .ql-editor .ql-font-verdana': { fontFamily: 'Verdana, sans-serif' },
                
                // Add specific styles for Quill font picker dropdown options
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=arial]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=arial]::before': { content: "\'Arial\'", fontFamily: 'Arial, sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=comic-sans-ms]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=comic-sans-ms]::before': { content: "\'Comic Sans MS\'", fontFamily: '\'Comic Sans MS\', cursive, sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=courier-new]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=courier-new]::before': { content: "\'Courier New\'", fontFamily: '\'Courier New\', Courier, monospace' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=georgia]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=georgia]::before': { content: "\'Georgia\'", fontFamily: 'Georgia, serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=helvetica]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=helvetica]::before': { content: "\'Helvetica\'", fontFamily: 'Helvetica, sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=lucida-sans]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=lucida-sans]::before': { content: "\'Lucida Sans\'", fontFamily: '\'Lucida Sans Unicode\', \'Lucida Grande\', sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=tahoma]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=tahoma]::before': { content: "\'Tahoma\'", fontFamily: 'Tahoma, sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=times-new-roman]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=times-new-roman]::before': { content: "\'Times New Roman\'", fontFamily: '\'Times New Roman\', Times, serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=trebuchet-ms]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=trebuchet-ms]::before': { content: "\'Trebuchet MS\'", fontFamily: '\'Trebuchet MS\', sans-serif' },
                '& .ql-snow .ql-picker.ql-font .ql-picker-label[data-value=verdana]::before, & .ql-snow .ql-picker.ql-font .ql-picker-item[data-value=verdana]::before': { content: "\'Verdana\'", fontFamily: 'Verdana, sans-serif' },
                
                // Ensure the Quill container layout is correct
                '& .ql-toolbar': {
                  borderTopLeftRadius: '8px',    // Match border radius
                  borderTopRightRadius: '8px',   // Match border radius
                  borderColor: 'rgba(0, 0, 0, 0.1)', // Match border color
                  borderBottom: 'none' // Remove bottom border from toolbar
                },
                '& .ql-container': {
                  borderBottomLeftRadius: '8px', // Match border radius
                  borderBottomRightRadius: '8px',// Match border radius
                  borderColor: 'rgba(0, 0, 0, 0.1)', // Match border color
                  minHeight: '150px' // Ensure editor area has min height
                },
                '& .ql-editor': {
                  minHeight: '150px', // Apply min height directly to editor area too
                  padding: '12px 15px' // Default Quill padding
                }
              }}> 
                <ReactQuill 
                  theme="snow" 
                  value={tweetText}
                  onChange={(value) => {
                    setTweetText(value);
                    if (onTextChange) {
                      onTextChange(value);
                    }
                  }}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter your tweet text here..."
                  className="tweet-editor" 
                />
              </Box>
            </>
          )}

          {/* Profile Settings */}
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1.5, 
              fontWeight: 600, 
              color: '#4A5568',
              fontSize: '0.95rem' 
            }}
          >
            Profile Settings
          </Typography>
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
              <Box sx={{ position: 'relative', mt: 0.5 }}>
              <Avatar 
                  src={tweetProfileImagePreview || linkedinProfileData?.profile_picture || userProfile?.image}
                  sx={{ width: 48, height: 48, border: '1px solid rgba(0, 0, 0, 0.08)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                >
                  {!tweetProfileImagePreview && !(linkedinProfileData?.profile_picture || userProfile?.image) && 
                    ((linkedinProfileData?.name || userProfile?.name || tweetName)?.[0]?.toUpperCase() || 'U')}
              </Avatar>
                <input accept="image/*" style={{ display: 'none' }} id="profile-image-upload" type="file" onChange={handleProfileImageChange} />
              <label htmlFor="profile-image-upload">
                <IconButton 
                  component="span"
                    size="small" 
                  sx={{ 
                    position: 'absolute',
                      bottom: -4, 
                      right: -4, 
                    bgcolor: '#4890fe',
                    color: 'white',
                      width: 22, 
                      height: 22, 
                    '&:hover': {
                      bgcolor: '#3a7ad9'
                    }
                  }}
                >
                    <UploadIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </label>
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: '#4A5568', fontSize: '0.85rem' }}>
                  Name
                </Typography>
              <TextField
                fullWidth
                size="small"
                value={tweetName}
                onChange={(e) => setTweetName(e.target.value)}
                  placeholder={linkedinProfileData?.name || userProfile?.name || "Enter your display name"}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      '&.Mui-focused fieldset': {
                        borderColor: '#4890fe',
                      },
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(72, 144, 254, 0.5)',
                      }
                    }
                  }} 
              />
            </Box>
          </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={6}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.75, 
                    fontWeight: 500, 
                    color: '#4A5568', 
                    fontSize: '0.85rem' 
                  }}
                >
                  Verification
              </Typography>
              <ToggleButtonGroup
                value={isVerified ? 'verified' : 'standard'}
                exclusive
                onChange={(e, value) => value && setIsVerified(value === 'verified')}
                size="small"
                  fullWidth
                  sx={{ 
                    '& .MuiToggleButton-root': {
                    textTransform: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      color: '#4A5568',
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                      py: 0.7,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(72, 144, 254, 0.08)',
                        color: '#4890fe',
                        borderColor: '#4890fe',
                        '&:hover': {
                          backgroundColor: 'rgba(72, 144, 254, 0.12)',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }
                  }}
                >
                  <ToggleButton value="verified">Verified</ToggleButton>
                  <ToggleButton value="standard">Standard</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={6}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 0.75, 
                    fontWeight: 500, 
                    color: '#4A5568', 
                    fontSize: '0.85rem' 
                  }}
                >
                  Timestamp
                </Typography>
            <TextField
                  fullWidth
              value={tweetTimestamp}
              onChange={(e) => setTweetTimestamp(e.target.value)}
              size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      '&.Mui-focused fieldset': {
                        borderColor: '#4890fe',
                      },
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(72, 144, 254, 0.5)',
                      }
                    }
                  }} 
                />
              </Grid>
            </Grid>
          </Box>
          {/* Add a little space at the very bottom of scroll */}
           <Box sx={{ height: '8px' }} />
        </Box>
      </Box>

      {/* Preview Panel - Only show if showPreview is true */}
      {showPreview && (
        <Box sx={{ flex: '1', display: 'flex', flexDirection: 'column', mt: 2, position: 'relative', width: showPreview ? '100%' : '50%' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#4A5568', display: 'flex', alignItems: 'center', gap: 1 }}>
            <TwitterIcon sx={{ fontSize: 20, color: '#1DA1F2' }} />
            Tweet Preview
          </Typography>
          <Box sx={{ flex: 1, border: '1px solid rgba(0, 0, 0, 0.05)', borderRadius: 2, overflow: 'hidden', position: 'relative', height: '100%', minHeight: 300 }}>
            {renderPreview()}
      </Box>
      </Box>
      )}
    </Box>
  );
});

export default TweetGenerator; 